/**
 * Marketplace Grid View
 * Card grid layout showing all marketplaces
 */

import React from 'react';
import { Settings } from 'lucide-react';
import type { CustomMarketplace } from './types';
import { MARKETPLACE_LOGOS } from './types';
import { useLanguage } from '../../i18n';

interface MarketplaceGridProps {
  marketplaces: CustomMarketplace[];
  onSelectMarketplace: (mp: CustomMarketplace) => void;
  onOpenSettings: (mp: CustomMarketplace) => void;
}

const MarketplaceGrid: React.FC<MarketplaceGridProps> = ({
  marketplaces,
  onSelectMarketplace,
  onOpenSettings,
}) => {
  const { t } = useLanguage();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-xl p-6 text-white">
        <h2 className="text-2xl font-bold mb-1">{t.marketplace.title}</h2>
        <p className="opacity-90">{t.marketplace.selectOrAdd}</p>
      </div>

      {/* Marketplace Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {marketplaces.map(mp => (
          <div
            key={mp.id}
            className="relative bg-white rounded-xl shadow-sm border-2 border-transparent hover:border-indigo-300 hover:shadow-md transition-all cursor-pointer group"
            style={{ borderTopColor: mp.color, borderTopWidth: '4px' }}
          >
            {/* Settings button */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                onOpenSettings(mp);
              }}
              className="absolute top-2 right-2 p-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <Settings className="w-4 h-4 text-slate-600" />
            </button>

            {/* Card content */}
            <div
              onClick={() => onSelectMarketplace(mp)}
              className="p-6 text-center"
            >
              {MARKETPLACE_LOGOS[mp.shortName] ? (
                <img
                  src={MARKETPLACE_LOGOS[mp.shortName]}
                  alt={mp.name}
                  className="w-12 h-12 mx-auto mb-3 object-contain"
                />
              ) : (
                <div className="text-4xl mb-3">{mp.icon}</div>
              )}
              <h3 className="font-semibold text-slate-800">{mp.name}</h3>
              <p className="text-xs text-slate-500 mt-1">
                {mp.countries.filter(c => c.isActive).length} {t.marketplace.countries} · {mp.columns.length} {t.marketplace.columns}
              </p>
              {!mp.isActive && (
                <span className="inline-block mt-2 px-2 py-0.5 bg-slate-100 text-slate-500 text-xs rounded">
                  {t.marketplace.inactive}
                </span>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Info Banner */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-800">
        {t.marketplace.note}
      </div>
    </div>
  );
};

export default MarketplaceGrid;
